import { useNavigate } from "@remix-run/react";
import type { KeyboardEventHandler } from "react";
import { useRef, useState } from "react";
import fullAddress from "~/kernel/adress/fullAddress";
import isEmpty from "~/kernel/arrays/isEmpty";
import ROUTES from "~/kernel/routing/ROUTES";
import className from "~/kernel/styling/className";
import useCompanySearch from "~/company/helpers/useCompanySearch";
import LinkList from "~/ui/LinkList";
import { getSelectedOption } from "~/ui/optionsNavigatorReducer";
import useOptionsNavigatorReducer from "~/ui/useOptionsNavigatorReducer";
import type { TCompanyListItem } from "../companies._index/TCompanyListItem";
import useTranslation from "~/kernel/internationalization/useTranslation";

export function DesktopSearchInput() {
  const { tt } = useTranslation("index");
  const { tt: ttHero } = useTranslation("hero");
  const [query, setQuery] = useState("");
  const fetcher = useCompanySearch(query);
  const companies = fetcher.data?.companies ?? [];
  const suggestionCount = 5;
  const suggestedCompanies = companies.slice(0, suggestionCount);
  const navigate = useNavigate();
  const [state, dispatch] = useOptionsNavigatorReducer();
  const ref = useRef<HTMLDivElement>(null);
  const options = companies.map((company) => ({
    value: ROUTES.company.path({ params: { slug: company.slug } }),
    label: company.name,
  }));
  const selectedOption = getSelectedOption(state, options);

  const navigateToResults = () => {
    if (selectedOption) {
      return navigate(selectedOption.value);
    }

    navigate(ROUTES.companies.path(state.query));
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (ev) => {
    switch (ev.key) {
      case "ArrowDown":
        return dispatch({ type: "arrowDown", options });
      case "ArrowUp":
        return dispatch({ type: "arrowUp", options });
      case "Enter":
        if (selectedOption) {
          return navigate(selectedOption.value);
        }

        return navigateToResults();
      default:
        return undefined;
    }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex flex-row">
        <input
          id="search-company"
          className="bg-white rounded-l-md xl:text-xl w-full py-3 px-5 border border-slate-200 border-r-0"
          placeholder={ttHero("searchPlaceholder")}
          aria-label={ttHero("searchPlaceholder")}
          onChange={(ev) => {
            setQuery(ev.target.value);
            dispatch({ type: "input", value: ev.target.value });
          }}
          onFocus={() => dispatch({ type: "focus" })}
          onBlur={(ev) => {
            const isOutside = !ref.current?.contains(ev.relatedTarget);

            if (isOutside) {
              dispatch({ type: "blur" });
            }
          }}
          onKeyDown={onKeyDown}
        />
        <button
          className="rounded-r-md bg-primary text-white py-3 px-5 whitespace-nowrap xl:text-xl hover:opacity-80 border-pink-600 border"
          onClick={navigateToResults}
        >
          <span>{tt("bookNow")}</span>
        </button>
      </div>
      <div
        className={className(
          "absolute top-[calc(100%+0.5rem)] w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl px-5",
          { "invisible opacity-0": !state.isEditMode },
        )}
      >
        <LinkList
          links={suggestedCompanies.map((company, index) => ({
            to: ROUTES.company.path({ params: { slug: company.slug } }),
            active: state.selectedIndex === index,
            children: (
              <CompanySearchSuggestion
                company={company}
                active={state.selectedIndex === index}
              />
            ),
          }))}
        />
        {isEmpty(companies) && <p className="p-5">{tt("noMatchingSalons")}</p>}
      </div>
    </div>
  );
}

type CompanySearchSuggestionProps = {
  company: TCompanyListItem;
  active: boolean;
};

function CompanySearchSuggestion({
  company,
  active,
}: CompanySearchSuggestionProps) {
  return (
    <div className="justify-center gap-3 md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,8fr)]">
      <div className="hidden md:block">
        <img
          className={className("w-full rounded-md border border-slate-100", {
            "border-white": active,
          })}
          src={company.bannerUrl}
          alt={`Banner of ${company.name}`}
        />
      </div>
      <div
        className={className({
          "text-white": active,
        })}
      >
        <p>{company.name}</p>
        <p className="text-xs">{fullAddress(company)}</p>
      </div>
    </div>
  );
}
